import { useMemo, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { consultarHistoricoDoProduto, listarMovimentacoes } from '../api/movimentacoes.js';
import { TAMANHO_PAGINA_PADRAO } from '../api/paginacao.js';
import { useConsulta } from '../api/useConsulta.js';
import { Botao } from '../componentes/Botao.js';
import { Cartao } from '../componentes/Cartao.js';
import { EstadoVazio } from '../componentes/EstadoVazio.js';
import { CampoBusca } from '../componentes/Filtros.js';
import { IconeBusca, IconeMovimentacoes } from '../componentes/Icones.js';
import { Pagina } from '../componentes/Pagina.js';
import { Resultado } from '../componentes/Resultado.js';
import { DepositoAtual } from './movimentacoes/DepositoAtual.js';
import { TrilhaDoProduto } from './movimentacoes/TrilhaDoProduto.js';
import './Rastreabilidade.css';

/** O código fica na URL: a trilha de um produto é link que se manda por chat. */
const PARAMETRO = 'codigo';

/**
 * Rastreabilidade de produto (RF-076, UC11).
 *
 * Responde à pergunta mais frequente do armazém — onde está este item e por
 * onde ele passou. O depósito atual vem em destaque; a trilha completa vem
 * abaixo, **paginada pelo servidor**, porque um produto de giro alto passa das
 * 5.000 movimentações que o RNF-006 manda responder em 3 segundos.
 *
 * São duas perguntas ao middleware de propósito: a do depósito atual é a
 * consulta de rastreio de UC11 (e é ela que fica registrada em auditoria); a
 * da trilha é uma listagem paginada como qualquer outra.
 */
export function Rastreabilidade() {
  const [parametros, definirParametros] = useSearchParams();
  const codigo = (parametros.get(PARAMETRO) ?? '').trim();

  const [rascunho, definirRascunho] = useState(codigo);
  const [pagina, definirPagina] = useState(1);

  const buscarHistorico = useMemo(
    () => (codigo === '' ? null : () => consultarHistoricoDoProduto(codigo)),
    [codigo],
  );

  const buscarTrilha = useMemo(
    () =>
      codigo === ''
        ? null
        : () => listarMovimentacoes({ produto: codigo, pagina, tamanho: TAMANHO_PAGINA_PADRAO }),
    [codigo, pagina],
  );

  const historico = useConsulta(buscarHistorico);
  const trilha = useConsulta(buscarTrilha);

  function aoRastrear(evento: SyntheticEvent<HTMLFormElement>) {
    evento.preventDefault();
    const pedido = rascunho.trim();

    definirPagina(1);
    definirParametros(pedido === '' ? {} : { [PARAMETRO]: pedido });
  }

  return (
    <Pagina
      contexto="Movimentações"
      titulo="Rastreabilidade de produto"
      voltarPara="/movimentacoes"
      filtros={
        <form className="rastreabilidade__busca" role="search" onSubmit={aoRastrear}>
          <CampoBusca
            rotulo="Buscar por código do produto"
            valor={rascunho}
            aoMudar={definirRascunho}
            largura={320}
          />
          <Botao type="submit" variante="primario" altura="media">
            Rastrear
          </Botao>
        </form>
      }
    >
      {codigo === '' ? (
        <Cartao semRespiro>
          <EstadoVazio
            tom="neutro"
            icone={<IconeBusca tamanho={28} />}
            titulo="Informe o código do produto."
            detalhe="A trilha mostra cada depósito por onde o item passou, com o operador responsável e o horário."
          />
        </Cartao>
      ) : (
        <>
          {trilha.dados !== null && trilha.dados.total > 0 && (
            <DepositoAtual consulta={historico} />
          )}

          <Resultado
            consulta={trilha}
            espera="Carregando a trilha do produto…"
            estaVazio={(dados) => dados.total === 0}
            vazio={
              <EstadoVazio
                tom="neutro"
                icone={<IconeMovimentacoes tamanho={28} />}
                titulo={`Sem histórico para ${codigo}.`}
                detalhe="Nenhuma movimentação foi registrada para este produto. Verifique se o item já foi inventariado."
              />
            }
          >
            {(dados) => <TrilhaDoProduto pagina={dados} aoIrPara={definirPagina} />}
          </Resultado>
        </>
      )}
    </Pagina>
  );
}
